/**
 * ProjectDetail — shell with the 10 project tabs from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/project-details.html
 *
 * Active tab is URL-driven via `?tab={key}` (default: transactions).
 * Project name lives in the breadcrumb (via `useSetBreadcrumb`).
 *
 * Tab status in this turn:
 *   ✓ Transactions  — full real-data tab (table + sidebar + preview + create)
 *   ⏳ Party · Site · Timeline · Attendances · Material
 *      Laminate · Files · Timesheet — placeholders for next turns
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';

import { useSetBreadcrumb } from '@/components/page/BreadcrumbContext';
import { useSetProjectHeader } from '@/components/chrome/ProjectHeaderContext';
import { CreateTransactionSlideOver } from '@/components/CreateTransactionSlideOver';
import { TransactionSlideOver } from '@/components/TransactionSlideOver';
import { db } from '@/lib/firebase';
import type { ProjectListItem } from '@/lib/useProjects';
import type { TransactionType } from '@/lib/transactions';
import { useVisibleProjectTabs } from '@/lib/useVisibleTabs';
import type { ProjectTabKey as SharedProjectTabKey } from '@shared/perms';
import { AttendanceTab } from '@/routes/projectDetail/AttendanceTab';
import { FilesTab } from '@/routes/projectDetail/FilesTab';
import { LaminateTab } from '@/routes/projectDetail/LaminateTab';
import { MaterialTab } from '@/routes/projectDetail/MaterialTab';
import { PartyTab } from '@/routes/projectDetail/PartyTab';
import { SiteTab } from '@/routes/projectDetail/SiteTab';
import { TimelineTab } from '@/routes/projectDetail/TimelineTab';
import { TransactionsTab } from '@/routes/projectDetail/TransactionsTab';
import { ProjectReadOnlyNotice } from '@/components/SeatLimitNotice';

// ───── Tab model (verbatim from the folder) ─────

type TabKey =
  | 'transactions'
  | 'party'
  | 'site'
  | 'timeline'
  | 'attendances'
  | 'material'
  | 'laminate'
  | 'files';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'transactions', label: 'Transactions' },
  { key: 'party',        label: 'Party' },
  { key: 'site',         label: 'Site' },
  { key: 'timeline',     label: 'Timeline' },
  { key: 'attendances',  label: 'Attendances' },
  { key: 'material',     label: 'Material' },
  { key: 'laminate',     label: 'Laminate' },
  { key: 'files',        label: 'Files' },
];

/** Web uses plural tab names; the shared per-role visibility map
 *  uses the mobile singular names. This map keeps the web URL +
 *  display strings unchanged while feeding role-based filtering
 *  through the shared `@shared/perms/visibleTabs` set. */
const WEB_TO_SHARED_TAB: Record<TabKey, SharedProjectTabKey> = {
  transactions: 'transaction',
  party:        'party',
  site:         'site',
  timeline:     'task',
  attendances:  'attendance',
  material:     'material',
  laminate:     'laminate',
  files:        'files',
};

const VALID_TAB_KEYS = new Set<TabKey>(TABS.map((t) => t.key));

function asTabKey(s: string | null): TabKey {
  if (s && VALID_TAB_KEYS.has(s as TabKey)) return s as TabKey;
  // Default landing tab. Overview was previously a tab but is now
  // its own page at `/projects/:id/edit` — opened from the
  // topbar's Edit button. Transactions is the most-used day-to-day
  // surface, so it owns the default.
  return 'transactions';
}

// ───── Component ─────

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [project, setProject] = useState<ProjectListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live subscription on the project doc.
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    return onSnapshot(
      doc(db, 'projects', projectId),
      (snap) => {
        if (!snap.exists()) setProject(null);
        else setProject({ ...(snap.data() as ProjectListItem), id: snap.id });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [projectId]);

  // Surface the project name in the breadcrumb.
  useSetBreadcrumb(project?.name ?? null);
  // Push the project doc into the global topbar so the chrome
  // can render name / progress / Share + Edit / avatar inline,
  // replacing the default search row while we're on this page.
  useSetProjectHeader(project);

  // Role-filtered tab list. Accountant sees only Transactions +
  // Party; Designer sees only design-stack tabs; etc. Mirrors the
  // mobile `useVisibleProjectTabs()` filter so both surfaces show
  // the same set.
  const visibleProjectTabs = useVisibleProjectTabs();
  const visibleTabs = useMemo(
    () => TABS.filter((t) => visibleProjectTabs.has(WEB_TO_SHARED_TAB[t.key])),
    [visibleProjectTabs],
  );

  // Active tab + slide-over state — all URL-driven so the back button
  // and deep-links behave naturally.
  const requestedTab = asTabKey(searchParams.get('tab'));
  // If the URL asks for a tab the current role can't see, fall back
  // to the first visible tab. Prevents a deep-link from pinning the
  // user on an empty Transactions tab they can't access.
  const activeTab: TabKey =
    visibleProjectTabs.has(WEB_TO_SHARED_TAB[requestedTab])
      ? requestedTab
      : visibleTabs[0]?.key ?? requestedTab;
  const openTxnId = searchParams.get('txn'); // 'new' OR a txn id OR null
  const createType = (searchParams.get('type') as TransactionType | null) ?? 'payment_out';

  const setTab = (next: TabKey) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    sp.delete('txn');
    sp.delete('type');
    setSearchParams(sp, { replace: true });
  };

  const openTransaction = (id: string) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'transactions');
    sp.set('txn', id);
    sp.delete('type');
    setSearchParams(sp);
  };

  const openCreateTransaction = (type: TransactionType) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'transactions');
    sp.set('txn', 'new');
    sp.set('type', type);
    setSearchParams(sp);
  };

  const closeTxnSlideOver = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete('txn');
    sp.delete('type');
    setSearchParams(sp);
  };

  const previewTxnId = openTxnId && openTxnId !== 'new' ? openTxnId : null;
  const isCreateOpen = openTxnId === 'new';

  // ───── Render ─────

  if (loading) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading project…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div
          className="card"
          style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}
        >
          Couldn't load project: {error}
        </div>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-folder-simple-dashed" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            Project not found
          </div>
          <button
            className="btn-primary"
            type="button"
            onClick={() => navigate('/projects')}
          >
            <i className="ph ph-arrow-left" /> Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page active"
      id="page-project-details"
      style={{ display: 'block' }}
    >
      {/* Project context (name / status / progress / Share / Edit /
          avatar) is rendered by the global TopBar via
          ProjectHeaderContext — see useSetProjectHeader() above.
          We deliberately don't render a hero card here so the
          two don't compete for vertical space. */}

      {/* Tab header (matches `.pd-header` + `.pd-tabs` from the folder) */}
      <div
        className="pd-header"
        style={{
          borderBottom: '1px solid var(--border)',
          paddingBottom: 0,
          marginBottom: 20,
        }}
      >
        <div className="pd-tabs">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`pd-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <ProjectReadOnlyNotice projectId={project.id} />

      {/* Tab content */}
      <div className="pd-content">
        {activeTab === 'transactions' ? (
          <TransactionsTab
            project={project}
            onOpenTransaction={openTransaction}
            onCreateTransaction={openCreateTransaction}
          />
        ) : activeTab === 'party' ? (
          <PartyTab
            project={project}
            onOpenTransaction={openTransaction}
          />
        ) : activeTab === 'site' ? (
          <SiteTab project={project} />
        ) : activeTab === 'timeline' ? (
          <TimelineTab project={project} />
        ) : activeTab === 'attendances' ? (
          <AttendanceTab project={project} />
        ) : activeTab === 'material' ? (
          <MaterialTab project={project} />
        ) : activeTab === 'laminate' ? (
          <LaminateTab project={project} />
        ) : activeTab === 'files' ? (
          <FilesTab project={project} />
        ) : (
          <TabPlaceholder tab={activeTab} />
        )}
      </div>

      {/* Slide-overs */}
      <TransactionSlideOver
        txnId={previewTxnId}
        onClose={closeTxnSlideOver}
      />
      <CreateTransactionSlideOver
        open={isCreateOpen}
        onClose={closeTxnSlideOver}
        orgId={project.orgId ?? null}
        projectId={project.id}
        type={createType}
        onCreated={(id) => openTransaction(id)}
      />
    </div>
  );
}

// ───── Placeholder for unbuilt tabs ─────

function TabPlaceholder({ tab }: { tab: TabKey }) {
  const labelByKey = useMemo(() => {
    const m: Record<TabKey, string> = TABS.reduce(
      (acc, t) => ({ ...acc, [t.key]: t.label }),
      {} as Record<TabKey, string>,
    );
    return m;
  }, []);
  const label = labelByKey[tab] ?? tab;
  return (
    <div className="card" style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
        <i className="ph ph-hourglass-medium" />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        {label} — coming next
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-secondary)',
          maxWidth: 460,
          margin: '0 auto',
        }}
      >
        Real-data {label.toLowerCase()} tab is being built in the next iteration.
        Tell Claude to tackle this tab when you're ready.
      </div>
    </div>
  );
}

